import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../constants';
import { useTranslation } from 'react-i18next';
import { createPlaceholderUrl } from '../../../utils/PlaceholderCreator';
import { useAuth } from '../../../context/AuthContext';

const SearchCostumeSkill = ({ isOpen, onClose, onAddSkill, cardId }) => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [skills, setSkills] = useState([]);
    const [filteredSkills, setFilteredSkills] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [category, setCategory] = useState('Unique');
    const [star, setStar] = useState(1);
    const [loading, setLoading] = useState(false);

    // Fetch all skills when modal opens
    useEffect(() => {
        if (isOpen) {
            const fetchSkills = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/skills`);
                    const data = await response.json();
                    setSkills(data);
                } catch (error) {
                    console.error("Error fetching skills:", error);
                }
            };
            fetchSkills();
            // Reset state
            setSearchTerm('');
            setSelectedSkill(null);
            setCategory('Unique');
            setStar(1);
        }
    }, [isOpen]);

    // Filter skills based on search term
    useEffect(() => {
        if (!searchTerm) {
            setFilteredSkills([]);
            return;
        }
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = skills.filter(skill =>
            (skill.Skill_name && skill.Skill_name.toLowerCase().includes(lowerSearch)) ||
            (skill.Skill_name_jp && skill.Skill_name_jp.toLowerCase().includes(lowerSearch))
        ).slice(0, 10); // Limit results
        setFilteredSkills(filtered);
    }, [searchTerm, skills]);

    const handleAdd = async () => {
        if (!selectedSkill) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/card_skills`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    card_id: cardId,
                    skill_id: selectedSkill.Id,
                    Category: category,
                    star: (category === 'Unique' || category === 'Awakening') ? parseInt(star) : null
                }),
            });

            if (response.ok) {
                onAddSkill(); // Callback to refresh parent
                onClose();
            } else {
                console.error("Failed to add skill");
            }
        } catch (error) {
            console.error("Error adding skill:", error);
        } finally {
            setLoading(false);
        }
    };

    const getSkillIcon = (iconId) => {
        return `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{t('SearchCostumeSkill.title')}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Search Section */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('SearchCostumeSkill.label_search')}</label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('SearchCostumeSkill.placeholder_search')}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-secondary-blue outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />

                    {/* Search Results */}
                    {filteredSkills.length > 0 && !selectedSkill && (
                        <div className="mt-2 border border-gray-200 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto bg-white dark:bg-gray-700">
                            {filteredSkills.map(skill => (
                                <div
                                    key={skill.Id}
                                    onClick={() => {
                                        setSelectedSkill(skill);
                                        setSearchTerm(''); // Clear search to hide list
                                    }}
                                    className="p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer flex items-center gap-3 border-b last:border-b-0 border-gray-100 dark:border-gray-600"
                                >
                                    <img
                                        src={getSkillIcon(skill.icon_id)}
                                        alt=""
                                        className="w-10 h-10 rounded"
                                        onError={(e) => e.target.src = createPlaceholderUrl(40, 40, '?')}
                                    />
                                    <div>
                                        <div className="font-semibold text-gray-800 dark:text-white">{skill.Skill_name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{skill.Skill_name_jp}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Selected Skill Configuration */}
                {selectedSkill && (
                    <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-6 border border-blue-100 dark:border-blue-800">
                        <div className="flex items-center gap-4 mb-4">
                            <img
                                src={getSkillIcon(selectedSkill.icon_id)}
                                alt=""
                                className="w-16 h-16 rounded shadow-sm"
                            />
                            <div>
                                <h3 className="font-bold text-lg text-gray-900 dark:text-white">{selectedSkill.Skill_name}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{selectedSkill.Skill_name_jp}</p>
                            </div>
                            <button
                                onClick={() => setSelectedSkill(null)}
                                className="ml-auto text-sm text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 underline"
                            >
                                {t('SearchCostumeSkill.button_change')}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('SearchCostumeSkill.label_category')}</label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-secondary-blue outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="Unique">{t('SearchCostumeSkill.option_unique')}</option>
                                    <option value="Innate">{t('SearchCostumeSkill.option_innate')}</option>
                                    <option value="Awakening">{t('SearchCostumeSkill.option_awakening')}</option>
                                    <option value="Events">{t('SearchCostumeSkill.option_events')}</option>
                                </select>
                            </div>

                            {(category === 'Unique' || category === 'Awakening') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {category === 'Unique' ? t('SearchCostumeSkill.label_stars') : t('SearchCostumeSkill.label_level')}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={star}
                                        onChange={(e) => setStar(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-secondary-blue outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        {t('SearchCostumeSkill.button_cancel')}
                    </button>
                    <button
                        onClick={handleAdd}
                        disabled={!selectedSkill || loading}
                        className={`px-6 py-2 rounded-lg text-white font-medium transition-colors ${!selectedSkill || loading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-secondary-blue hover:bg-blue-600'
                            }`}
                    >
                        {loading ? t('SearchCostumeSkill.button_saving') : t('SearchCostumeSkill.button_add')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SearchCostumeSkill;
// Revisado: 2025-12-18 [I18n Scan]
